import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type Props = {
  open: boolean
  onClose: () => void
  children: ReactNode
  width?: number
  align?: 'left' | 'right'
}

const GAP = 4 // breathing room between the trigger and the panel
const EDGE = 8 // never come closer than this to a viewport edge
const MIN_HEIGHT = 120 // below this a capped panel is useless; let it overhang instead

type Pos = { top: number; left: number; maxHeight: number }

// Above the drawer (41) and the hand-rolled modals (201), below toasts.
const Z = 250

export function Popover({ open, onClose, children, width = 280, align = 'left' }: Props) {
  // The anchor is a zero-size marker left behind in the original DOM slot: the
  // panel itself is portalled to <body> so no `overflow: auto` ancestor (the
  // drawer body, a scrolling table) can clip it, and no `transform` ancestor
  // (the planning modal) can hijack its containing block.
  const anchorRef = useRef<HTMLSpanElement>(null)
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<Pos | null>(null)

  const place = useCallback(() => {
    const anchor = anchorRef.current?.parentElement
    const panel = ref.current
    if (!anchor || !panel) return

    const a = anchor.getBoundingClientRect()
    const h = panel.getBoundingClientRect().height
    const { innerWidth: vw, innerHeight: vh } = window

    // Flip up only when the panel genuinely doesn't fit below and there's more
    // room above. A capped panel re-measures to its cap, which keeps this
    // decision stable across repositions.
    const below = vh - a.bottom - GAP - EDGE
    const above = a.top - GAP - EDGE
    const dropDown = h <= below || below >= above

    const top = dropDown ? a.bottom + GAP : Math.max(EDGE, a.top - GAP - h)
    const rawLeft = align === 'right' ? a.right - width : a.left
    const next: Pos = {
      top,
      left: Math.max(EDGE, Math.min(rawLeft, vw - width - EDGE)),
      maxHeight: Math.max(MIN_HEIGHT, dropDown ? below : above),
    }

    // Bail out on an unchanged result: capping maxHeight resizes the panel,
    // which wakes the ResizeObserver, which calls place() again. Without this
    // the pair could spin.
    setPos((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.maxHeight === next.maxHeight
        ? prev
        : next,
    )
  }, [align, width])

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (ref.current?.contains(target)) return
      // The trigger (chip button) is the anchor marker's sibling under a shared
      // positioned parent. Let clicks inside that parent fall through to
      // the trigger; otherwise the trigger would re-toggle the popover open
      // and the clear (x) button on the chip would never get a chance to run.
      const parent = anchorRef.current?.parentElement
      if (parent?.contains(target)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    // Defer registration so the click that opened the popover doesn't
    // immediately get treated as an "outside" click and close it.
    const id = window.setTimeout(() => {
      document.addEventListener('click', onDocClick)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  // Measure before paint, so the panel never flashes at its pre-placement spot.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    place()
    // `capture` catches scrolls in the drawer body and other inner containers,
    // which don't bubble.
    const onScroll = () => place()
    window.addEventListener('scroll', onScroll, { capture: true, passive: true })
    window.addEventListener('resize', onScroll)

    // The panel changes height as its content does (a filtered select list),
    // which moves its top edge when it was flipped up.
    const ro =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => place())
    if (ro && ref.current) ro.observe(ref.current)

    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true })
      window.removeEventListener('resize', onScroll)
      ro?.disconnect()
    }
  }, [open, place])

  if (!open) return null
  return (
    <>
      <span ref={anchorRef} aria-hidden style={{ display: 'none' }} />
      {createPortal(
        <div
          ref={ref}
          data-popover=""
          style={{
            position: 'fixed',
            // Measure the panel's natural height offscreen on the first pass.
            top: pos ? pos.top : -9999,
            left: pos ? pos.left : -9999,
            visibility: pos ? 'visible' : 'hidden',
            maxHeight: pos?.maxHeight,
            width,
            background: 'var(--bg-2)',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-pop)',
            zIndex: Z,
            overflowX: 'hidden',
            overflowY: 'auto',
            animation: 'pop-in 180ms cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  )
}
